import type { ApiService } from '@/api'
import type { Subscription, SubscriptionSKU } from '@/types/subscriptions'
import type { IFusionAuth } from '@fusion/auth-v3'

export interface EnrichedSubscription extends Subscription {
  totalQty: number
  totalQtyRemaining: number
  totalUsed: number
  used: number[]
}

export interface ModelQtyRemainingPayload {
  qtyRemaining: number
  model: string
}

export interface ModelQtyUsedPayload {
  qtyUsed: number
  model: string
}

export interface UpdateSubscriptionsData {
  accountId?: string
  auth: IFusionAuth
  api: ApiService
}

export interface SubscriptionsState {
  SKUMetadata: Record<string, SubscriptionSKU> | null
  subscriptions: Subscription[]
  genericTSILink: EnrichedSubscription[]
  website: EnrichedSubscription[]
  basic: EnrichedSubscription[]
  premium: EnrichedSubscription[]
  websiteQtyRemainingBlueSky: number
  websiteQtyUsedBlueSky: number
  websiteQtyRemainingBlueSkyGas: number
  websiteQtyUsedBlueSkyGas: number
  websiteQtyRemainingAirAssure: number
  websiteQtyUsedAirAssure: number
  basicQtyRemainingBlueSky: number
  basicQtyRemainingAirAssure: number
  premiumQtyRemainingBlueSky: number
  premiumQtyRemainingBlueSkyGas: number
  premiumQtyRemainingAirAssure: number
  dataServices: EnrichedSubscription[]
  deviceWebsite: Record<string, string> | null
  deviceGeneric: Record<string, string> | null
  deviceBasic: Record<string, string> | null
  devicePremium: Record<string, string> | null
  deviceDataServices: Record<string, string> | null
  permissionsInRole: Record<string, string[]>
}

export enum SUBSCRIPTIONS_MUTATIONS {
  UPDATE_SKU_METADATA = 'updateSKUMetadata',
  UPDATE_SUBSCRIPTIONS = 'updateSubscriptions',
  UPDATE_GENERIC_TSI_LINK = 'updateGenericTSILink',
  UPDATE_WEBSITE = 'updateWebsite',
  UPDATE_BASIC = 'updateBasic',
  UPDATE_PREMIUM = 'updatePremium',
  UPDATE_DEVICE_WEBSITE = 'updateDeviceWebsite',
  UPDATE_DEVICE_GENERIC = 'updateDeviceGeneric',
  UPDATE_DEVICE_BASIC = 'updateDeviceBasic',
  UPDATE_DEVICE_PREMIUM = 'updateDevicePremium',
  UPDATE_DEVICE_DATA_SERVICES = 'updateDeviceDataServices',
  UPDATE_WEBSITE_QTY_USED = 'updateWebsiteQtyUsed',
  UPDATE_WEBSITE_QTY_REMAINING = 'updateWebsiteQtyRemaining',
  UPDATE_BASIC_QTY_REMAINING = 'updateBasicQtyRemaining',
  UPDATE_PREMIUM_QTY_REMAINING = 'updatePremiumQtyRemaining',
  UPDATE_DATA_SERVICES = 'updateDataServices'
}

export enum SUBSCRIPTIONS_ACTIONS {
  UPDATE_SUBSCRIPTIONS = 'updateSubscriptions'
}

export enum SUBSCRIPTIONS_GETTERS {
  GET_SKU_METADATA = 'getSKUMetadata',
  GET_SKU_METADATA_BY_ID = 'getSKUMetadataById',
  GET_SKU_METADATA_NAME_BY_ID = 'getSKUMetadataNameById',
  GET_SKU_METADATA_TIER_NAME_BY_ID = 'getSKUMetadataTierNameById',
  GET_SKU_METADATA_TYPE_NAME_BY_ID = 'getSKUMetadataTypeNameById',
  GET_GENERIC_TSI_LINK = 'getGenericTSILink',
  GET_WEBSITE = 'getWebsite',
  GET_BASIC = 'getBasic',
  GET_PREMIUM = 'getPremium',
  GET_DATA_SERVICES = 'getDataServices',
  GET_DEVICE_WEBSITE = 'getDeviceWebsite',
  GET_DEVICE_BASIC = 'getDeviceBasic',
  GET_DEVICE_PREMIUM = 'getDevicePremium',
  GET_DEVICE_DATA_SERVICES = 'getDeviceDataServices',
  GET_DEVICE_SUBSCRIPTION_BY_DEVICE_SERIAL = 'getDeviceSubscriptionByDeviceSerial',
  GET_DEVICE_BASIC_SUBSCRIPTION_BY_DEVICE_SERIAL = 'getDeviceBasicSubscriptionByDeviceSerial',
  GET_DEVICE_PREMIUM_SUBSCRIPTION_BY_DEVICE_SERIAL = 'getDevicePremiumSubscriptionByDeviceSerial',
  GET_DEVICE_DATA_SERVICES_BY_DEVICE_SERIAL = 'getDeviceDataServicesByDeviceSerial',
  GET_SUBSCRIPTION_BY_ID = 'getSubscriptionById',
  GET_QTY_REMAINING = 'getQtyRemaining',
  HAS_QTY_REMAINING = 'hasQtyRemaining',
  HAS_DATA_SERVICES_SUBSCRIPTION = 'hasDataServicesSubscription',
  HAS_WEBSITE_SUBSCRIPTION = 'hasWebsiteSubscription',
  CAN_ADD_DEVICE_TO_WEBSITE_BY_MODEL = 'canAddDeviceToWebsiteByModel',
  CAN_ADD_DEVICE_TO_BASIC_BY_MODEL = 'canAddDeviceToBasicByModel',
  CAN_ADD_DEVICE_TO_PREMIUM_BY_MODEL = 'canAddDeviceToPremiumByModel',
  GET_PERMISSIONS_BY_ROLE = 'getPermissionsByRole'
}
